import { type ChangeEventHandler, type FC, useState } from 'react';
import { useUnit } from 'effector-react';
import { Flex, Input, MultiSelect, Spoiler, Stack, Title } from '@mantine/core';
import { DateTimePicker, type DateTimePickerProps } from '@mantine/dates';
import { type PayoutOrderPublicStatus } from '~/entities/payout';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { isUuid } from '~/shared/lib/utils/isUuid';
import { RequisiteFilter } from './components';
import { statuses } from './constants';
import { $filters, setFilters } from './model';

interface FiltersProps {
    onReset?: VoidFunction;
}

export const Filters: FC<FiltersProps> = ({ onReset }) => {
    const { filters } = useUnit({
        filters: $filters,
    });

    const isMobile = useBreakpoint('mobile');
    const [orderIdValue, setOrderIdValue] = useState(filters.orderId?.value ?? '');

    const isOrderIdInvalid = orderIdValue !== '' && !isUuid(orderIdValue);

    const handleClearOrderId = () => {
        setOrderIdValue('');
        setFilters({
            orderId: {
                value: undefined,
            },
        });
        onReset?.();
    };

    const handleChangeOrderId: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        setOrderIdValue(value);

        if (value === '') {
            handleClearOrderId();

            return;
        }

        if (!isUuid(value)) {
            return;
        }

        setFilters({
            orderId: {
                value,
            },
        });
        onReset?.();
    };

    const handleChangePublicStatuses = (value: string[]) => {
        if (value.length === 0) {
            setFilters({
                publicStatuses: statuses.map((status) => status.value),
            });
        } else {
            setFilters({
                publicStatuses: value as PayoutOrderPublicStatus[],
            });
        }

        onReset?.();
    };

    const handleChangeCreatedAtGt: DateTimePickerProps['onChange'] = (value) => {
        setFilters({
            createdAtGt: value ? new Date(value).toISOString() : undefined,
        });
        onReset?.();
    };

    const handleChangeCreatedAtLt: DateTimePickerProps['onChange'] = (value) => {
        setFilters({
            createdAtLt: value ? new Date(value).toISOString() : undefined,
        });
        onReset?.();
    };

    return (
        <Stack gap={4}>
            <Title
                order={2}
                fz='sm'
                fw={700}
                lh={1.2}
            >
                Фильтры
            </Title>

            <Stack gap={4}>
                <Input.Wrapper
                    size='xs'
                    label='ID ордера'
                    error={isOrderIdInvalid ? 'Введите корректный ID' : undefined}
                >
                    <Input
                        size='xs'
                        placeholder='Введите ID ордера'
                        value={orderIdValue}
                        onChange={handleChangeOrderId}
                        rightSection={orderIdValue ? <Input.ClearButton onClick={handleClearOrderId} /> : undefined}
                        rightSectionPointerEvents='auto'
                    />
                </Input.Wrapper>

                <MultiSelect
                    searchable
                    placeholder={filters.publicStatuses?.length !== statuses.length ? 'Выберите статус' : ''}
                    value={filters.publicStatuses}
                    data={statuses}
                    label='Статус ордеров'
                    size='xs'
                    onChange={handleChangePublicStatuses}
                />
            </Stack>

            <Spoiler
                maxHeight={0}
                showLabel='Показать больше фильтров'
                hideLabel='Скрыть'
            >
                <Stack gap={4}>
                    <Flex
                        gap={!isMobile ? 8 : 4}
                        direction={!isMobile ? 'row' : 'column'}
                    >
                        <RequisiteFilter />

                        <DateTimePicker
                            clearable
                            size='xs'
                            flex={1}
                            miw={240}
                            value={filters.createdAtGt}
                            placeholder='Выберите дату и время'
                            label='Создано от'
                            onChange={handleChangeCreatedAtGt}
                        />

                        <DateTimePicker
                            clearable
                            size='xs'
                            flex={1}
                            miw={240}
                            value={filters.createdAtLt}
                            placeholder='Выберите дату и время'
                            label='Создано до'
                            onChange={handleChangeCreatedAtLt}
                        />
                    </Flex>
                </Stack>
            </Spoiler>
        </Stack>
    );
};
